import type Highcharts from 'highcharts';

import type { DChartInterface } from 'webapp-types';

import AnnotationsGridHandler from 'webapp/d-chart/annotations/grid';
import type DChart from 'webapp/d-chart/d-chart';
import GridRenderer from 'webapp/d-chart/renderer/grid';
import LineOrBarChartRenderer from 'webapp/d-chart/renderer/line-or-bar-chart';

export default class SimpleChartRenderer extends GridRenderer {
	constructor(chart: DChart) {
		super(chart);
		if (this.showAnnotations) {
			this.initAnnotationsHandler();
		}
	}

	override get visualization() {
		return super.visualization;
	}

	override get series() {
		const serie = super.series[0];
		return serie ? [serie] : [];
	}

	get hasLegend() {
		return false;
	}

	override get baseOptions(): Highcharts.Options {
		let options = super.baseOptions;

		options = this.mergeOptions(options, {
			xAxis: {
				visible: false,
				minPadding: 0,
				maxPadding: 0,
				crosshair: {
					width: 1,
				},
			},
			yAxis: [
				{
					visible: false,
				},
			],
			legend: {
				enabled: this.hasLegend,
				labelFormatter: undefined, // overwrite from grid
			},
		});

		return options;
	}

	override get sizeOptions(): Highcharts.Options {
		let options = super.sizeOptions;

		options = this.mergeOptions(options, {
			chart: {
				spacing: [0, 0, 0, 0],
				spacingTop: 0,
				// to not cut off first/last points (esp in case there's just one) + hover markers
				// set seperately to correctly overwrite parent's customizations
				marginLeft: 5,
				marginRight: 5,
				marginBottom: 5,
				marginTop: 5,
			},
			xAxis: {
				minPadding: 0,
				maxPadding: 0,
			},
			plotOptions: {
				series: {
					clip: false, // to not clip series (helps against hover marker cutoff)
					marker: {
						radius: 1,
						states: {
							hover: {
								radius: 5,
							},
						},
					},
					states: {
						hover: {
							halo: null,
						},
					},
				},
			},
		});

		return options;
	}

	override get themeOptions(): Highcharts.Options {
		let options = super.themeOptions;

		const defs: Record<string, DChartInterface.Def> = {};

		this.getDefs().forEach((def) => {
			defs[def.id] = def;
		});

		options = this.mergeOptions(options, { defs });

		return options;
	}

	override get dataOptions(): Highcharts.Options {
		const options = super.dataOptions;

		const yAxis = (options.yAxis as Highcharts.YAxisOptions[] | undefined)?.[0]; // grid's yAxis is an array
		if (yAxis) {
			yAxis.startOnTick = false;
			yAxis.endOnTick = false;
		}

		return options;
	}

	get mainGradientDefs(): DChartInterface.Def[] {
		const themeData = this.dChart.themeData;
		return [
			...LineOrBarChartRenderer.getAreaGradientDefs(themeData), // when one primary main
		];
	}

	override updateTheme(): void {
		let options = this.themeOptions;

		// merge with data options since they set colorIndex
		options = this.mergeOptions(options, this.dataOptions);

		this.dChart.updateHChart(options);

		this.updateDefs();
	}

	protected override getDefs() {
		return this.mainGradientDefs;
	}

	protected override initAnnotationsHandler(): void {
		this.annotationsHandler = new AnnotationsGridHandler(this);
	}
}
